import { useEffect, useState, useRef, useMemo } from "react";
import { CASE_STATUS_SDK } from "lib/constants";
import { SvgGoogle, SvgSwicthSSO } from "ui/svgs";
import { CaseInfoType, AlertDataType } from "lib/types";
import { Table, Tooltip } from "antd";
import Cookies from "js-cookie";
import moment from "moment";
import clsx from "clsx";
//import { gapi } from "gapi-script";

import useUploadToDriveService, {
  CHUNK_SIZE,
} from "pages/dashboard/cases/drive-google-service";
import { Modal } from "antd";
import { captureFramesFromVideo } from "lib/utils";
import { showToast } from "ui/components/common";

import { useService } from "pages/dashboard/alerts/service";
import { pdf } from "@react-pdf/renderer";
import ExportCase from "ui/cases/export-case-document";

type TaskType = {
  status:
    | "Pending"
    | "PreLoading"
    | "Downloaded"
    | "Uploading"
    | "Uploaded"
    | "DownloadFailed"
    | "UploadFailed";
  uploadingProgress?: number;
  type: "pdf" | "alert" | "archive";
  progress?: number;
  id: string; // pdf为caseid,alert为alertid,archive为archiveid
  fileName: string;
  url?: string; // Only for alert and archive
  size?: number; // Only for alert and archive
  processed?: number; // download current size or upload current size
  pdfBlob?: Blob; // Only for pdf
  uploadUrl?: string;
};

export default function UploadToDriveModal(props: {
  caseInfo: CaseInfoType;
  open: boolean;
  onClose: () => void;
}) {
  const { caseInfo, open, onClose } = props;
  const caseIdRef = useRef(caseInfo.id);
  const {
    email,
    tokenWillExpire,
    loginGoogle,
    getGoogleToken,
    searchFolderProperty,
    createFolder,
    fetchS3File,
    doUploadChunk,
    startUpload,
    getOneChunkFromDb,
  } = useUploadToDriveService(open);

  const openRef = useRef(open);

  const { getSnapShotByAlertObj } = useService();
  const [tasks, setTasks] = useState<TaskType[]>([]);
  const [downloadingTask, setDownloadingTask] = useState<TaskType>();
  const [uploadingTask, setUploadingTask] = useState<TaskType>();

  const [caseFolderId, setCaseFolderId] = useState("");
  const [alertsFolderId, setAlertsFolderId] = useState("");
  const [archivesFolderId, setArchivesFolderId] = useState("");

  const [isUploading, setIsUploading] = useState(false);

  const [alertSnapShotSrcMap, setAlertSnapShotSrcMap] = useState<
    Map<string, string>
  >(new Map());
  const [archiveSnapshotMap, setArchiveSnapshotMap] = useState<
    Map<string, string>
  >(new Map());
  const [existConfirmModalOpen, setExistConfirmModalOpen] = useState(false);
  const [reAuthModalOpen, setReAuthModalOpen] = useState(false);

  useEffect(() => {
    openRef.current = open;
    if (!open) {
      setIsUploading(false);
      setTasks([]);
      setCaseFolderId("");
      setAlertsFolderId("");
      setArchivesFolderId("");
      setUploadingTask(null);
      setDownloadingTask(null);
      setAlertSnapShotSrcMap(new Map());
      setArchiveSnapshotMap(new Map());
      return;
    }
  }, [open]);

  useEffect(() => {
    if (!isUploading || globalState === "completed") {
      return;
    }

    setReAuthModalOpen(tokenWillExpire);
  }, [tokenWillExpire, isUploading]);

  useEffect(() => {
    if (!caseInfo || !open || !isUploading) {
      return;
    }
    setAlertSnapShotSrcMap(new Map());
    setArchiveSnapshotMap(new Map());
    const tasks: TaskType[] = [];
    const pdfTask: TaskType = {
      status: "Pending",
      type: "pdf",
      id: caseInfo.id,
      fileName: `Case_${caseInfo.displayId}.pdf`,
      processed: 0,
    };
    tasks.push(pdfTask);
    const testUrl =
      "http://mirror.aarnet.edu.au/pub/TED-talks/911Mothers_2010W-480p.mp4";
    caseInfo?.alerts?.forEach((alert) => {
      if (!alert.video?.url) {
        return;
      }
      const alertTask: TaskType = {
        status: "Pending",
        type: "alert",
        id: alert.id,
        fileName: `${alert.camera?.name}_${moment(Number(alert.createdAt)).format("YY_MM_DD_HH_mm")}.mp4`,
        url: alert.video?.url,
        processed: 0,
      };
      tasks.push(alertTask);
    });

    caseInfo?.archives?.forEach((archive) => {
      if (!archive.s3Files?.at(0)?.url || archive.status !== "succeed") {
        return;
      }
      const archiveTask: TaskType = {
        status: "Pending",
        type: "archive",
        id: archive.id,
        url: archive?.s3Files?.at(0)?.url,
        fileName: `${archive.uploadParams?.cameraName}_${moment(Number(archive.createdAt) * 1000).format("YY_MM_DD_HH_mm")}.mp4`,
        processed: 0,
      };
      tasks.push(archiveTask);
    });
    setTasks(tasks);
    const captureArchiveFrames = async () => {
      const archiveSnapshotMap = new Map<string, string>();
      for (const archive of caseInfo.archives) {
        if (archive.status !== "succeed") {
          continue;
        }
        const data = await captureFramesFromVideo(archive?.s3Files?.at(0)?.url);
        archiveSnapshotMap.set(archive.id, data);
      }

      setArchiveSnapshotMap(archiveSnapshotMap);
    };
    captureArchiveFrames();
    caseInfo.alerts.forEach((alert) => {
      getSnapShotByAlertObj(alert, { case_id: caseInfo?.id })
        .then((data) => {
          if (caseIdRef.current !== caseInfo?.id) {
            return;
          }
          setAlertSnapShotSrcMap((pre) => {
            pre.set(alert.id, URL.createObjectURL(data));
            return new Map(pre);
          });
        })
        .catch((e) => {
          if (caseIdRef.current !== caseInfo?.id) {
            return;
          }
          setAlertSnapShotSrcMap((pre) => {
            pre.set(alert.id, "");
            return new Map(pre);
          });
        });
    });
  }, [caseInfo, open, isUploading]);

  useEffect(() => {
    if (!isUploading) {
      return;
    }

    initDriveFolder();
  }, [isUploading]);

  useEffect(() => {
    if (
      !caseFolderId ||
      !alertsFolderId ||
      !archivesFolderId ||
      tasks.length === 0 ||
      !open
    ) {
      return;
    }
    if (!downloadingTask) {
      const nextTask = tasks?.find((task) => task.status === "Pending");

      nextTask && setDownloadingTask(nextTask);
    }
    if (!uploadingTask) {
      const nextTask = tasks?.find((task) => task.status === "Downloaded");
      nextTask && setUploadingTask(nextTask);
    }
  }, [
    caseFolderId,
    alertsFolderId,
    archivesFolderId,
    tasks,
    downloadingTask,
    uploadingTask,
  ]);

  useEffect(() => {
    if (!downloadingTask) {
      return;
    }
    const successArchiveCount = caseInfo?.archives?.filter(
      (archive) => archive.status === "succeed",
    ).length;
    if (
      downloadingTask.type === "pdf" &&
      (alertSnapShotSrcMap.size < caseInfo?.alerts?.length ||
        archiveSnapshotMap.size < successArchiveCount)
    ) {
      return;
    }
    updateOneTask(downloadingTask.id, { status: "PreLoading" });
    const doPreloadTask = async () => {
      try {
        if (downloadingTask.type === "pdf") {
          const blob = await pdf(
            <ExportCase
              caseInfo={caseInfo}
              alertSnapshotMap={alertSnapShotSrcMap}
              archiveSnapshotMap={archiveSnapshotMap}
            />,
          ).toBlob();
          updateOneTask(downloadingTask.id, {
            status: "Downloaded",
            pdfBlob: blob,
          });
        } else {
          const videoSize = await fetchS3File(
            caseInfo.id,
            downloadingTask.id,
            downloadingTask.url,
            downloadingTask.processed || 0,
            (receive, total) => {
              onDownloadProgress(receive, total, downloadingTask.id);
            },
          );
          updateOneTask(downloadingTask.id, {
            status: "Downloaded",
            size: videoSize,
            progress: 0,
            processed: 0,
          });
        }
        setDownloadingTask(null);
      } catch (error) {
        console.error("connect error:", error);
        updateOneTask(downloadingTask.id, { status: "DownloadFailed" });
      }
      setDownloadingTask(null);
    };
    doPreloadTask();
  }, [downloadingTask, alertSnapShotSrcMap, archiveSnapshotMap]);

  useEffect(() => {
    if (!uploadingTask) {
      return;
    }

    updateOneTask(uploadingTask.id, { status: "Uploading" });

    const doUploadTask = async () => {
      try {
        if (uploadingTask.type === "pdf") {
          await uploadPdf(uploadingTask);
        } else {
          await uploadVideo(uploadingTask);
        }

        updateOneTask(uploadingTask.id, { status: "Uploaded" });
      } catch (error) {
        if (error.message === "Token Expired") {
          Cookies.remove("googleTokenExpiresAt");
          setIsUploading(false);
          setTasks([]);
          setUploadingTask(null);
          setDownloadingTask(null);
          setAlertSnapShotSrcMap(new Map());
          setArchiveSnapshotMap(new Map());
          return;
        }
        if (error.message === "The session has expired") {
          updateOneTask(uploadingTask.id, {
            status: "UploadFailed",
            progress: 0,
            processed: 0,
          });
          return;
        }
        updateOneTask(uploadingTask.id, { status: "UploadFailed" });
      }

      setUploadingTask(null);
    };

    doUploadTask();
  }, [uploadingTask]);

  const retryAllFail = () => {
    setTasks((pre) => {
      return pre.map((task) => {
        if (task.status === "DownloadFailed") {
          return { ...task, status: "Pending" };
        }
        if (task.status === "UploadFailed") {
          return { ...task, status: "Downloaded" };
        }
        return task;
      });
    });
  };

  const globalState = useMemo(() => {
    if (!isUploading) {
      return "upload";
    }
    if (tasks.every((task) => task.status === "Uploaded")) {
      return "completed";
    }
    if (
      tasks.some((task) => task.status === "Pending") ||
      tasks.some((task) => task.status === "PreLoading") ||
      tasks.some((task) => task.status === "Uploading")
    ) {
      return "uploading";
    }

    return "retry";
  }, [tasks, isUploading]);

  const uploadVideo = async (task: TaskType) => {
    const metadata = {
      name: task.fileName,
      mimeType: "video/mp4",
      properties: {
        //agiCasePdf: "lzc", // 自定义数据
      },
      parents: task.type === "alert" ? [alertsFolderId] : [archivesFolderId],
    };

    let uploadUrl;
    let chunkIndex;
    let start;
    let end;
    const token = await getGoogleToken();
    if (task.processed === 0 && !task.uploadUrl) {
      const createResponse = await startUpload(token, metadata);
      checkRes(createResponse);
      uploadUrl = createResponse.headers.location;
      chunkIndex = 0;
      start = 0;
      end = CHUNK_SIZE;
      updateOneTask(task.id, { uploadUrl });
    } else {
      uploadUrl = task.uploadUrl;
      start = task.processed;
      end = start + CHUNK_SIZE;
      chunkIndex = Math.floor(start / CHUNK_SIZE);
    }

    while (start < task.size) {
      if (end > task.size) end = task.size;
      const dbData = await getOneChunkFromDb(
        caseInfo.id,
        task.id,
        chunkIndex++,
      );
      if (!dbData) {
        break;
      }
      const data = (dbData as { data: Uint8Array }).data;
      const len = data.length;
      // const combined = Uint8Array.from([...lastChunkData, ...data]);

      [start, end] = await uploadChunk(
        task.id,
        uploadUrl,
        data,
        start,
        end - 1,
        task.size,
      );
      end = start + CHUNK_SIZE;
    }
  };

  const uploadPdf = async (task: TaskType) => {
    const metadata = {
      name: task.fileName,
      mimeType: task.pdfBlob.type,
      properties: {},
      parents: [caseFolderId], // 指定上传到的文件夹 ID
    };
    let uploadUrl;
    let start;
    let end;
    const googleToken = await getGoogleToken();
    if (task.processed === 0 && !task.uploadUrl) {
      const createResponse = await startUpload(googleToken, metadata);
      checkRes(createResponse);
      uploadUrl = createResponse.headers.location;

      start = 0;
      end = CHUNK_SIZE;
      updateOneTask(task.id, { uploadUrl });
    } else {
      uploadUrl = task.uploadUrl;
      start = task.processed;
      end = start + CHUNK_SIZE;
    }

    while (start < task.pdfBlob.size) {
      if (end > task.pdfBlob.size) end = task.pdfBlob.size;

      const chunk = task.pdfBlob.slice(start, end);

      [start, end] = await uploadChunk(
        task.id,
        uploadUrl,
        chunk,
        start,
        end - 1,
        task.pdfBlob.size,
      );
    }

    console.log("Upload complete!");
  };

  const updateOneTask = (taskId, newProperty) => {
    setTasks((pre) => {
      return pre.map((task) => {
        if (task.id === taskId) {
          return { ...task, ...newProperty };
        }
        return task;
      });
    });
  };

  const onUploadProgress = (current, total, taskId) => {
    const progress = Math.round((current / total) * 100);
    updateOneTask(taskId, { processed: current, progress });
  };

  const onDownloadProgress = (current, total, taskId) => {
    const progress = Math.round((current / total) * 100);
    updateOneTask(taskId, { processed: current, progress });
  };

  const uploadChunk = async (taskId, url, chunk, start, end, totalSize) => {
    const maxRetries = 3; // 最大重试次数
    let attempts = 0;
    if (!openRef.current) {
      throw new Error("Cancel");
    }
    while (attempts < maxRetries) {
      try {
        const googleToken = await getGoogleToken();
        const chunkRes = await doUploadChunk(
          googleToken,
          url,
          chunk,
          start,
          end,
          totalSize,
        );
        console.log(
          "chunkRes.status",
          chunkRes.status,
          chunkRes.headers?.range,
        );
        switch (chunkRes.status) {
          case 308: {
            const range = chunkRes.headers?.range;
            const newStart = Number(range?.split("-")?.at(1)) + 1;
            onUploadProgress(newStart, totalSize, taskId);
            if (totalSize - newStart < CHUNK_SIZE * 2) {
              return [newStart, totalSize];
            }

            return [newStart, newStart + CHUNK_SIZE];
          }
          case 200:
          case 201: {
            const newStart = end + 1;
            onUploadProgress(newStart, totalSize, taskId);
            if (totalSize - newStart < CHUNK_SIZE * 2) {
              return [newStart, totalSize];
            }

            return [newStart, newStart + CHUNK_SIZE];
          }
          case 401: {
            attempts = maxRetries;
            throw new Error("Token Expired");
          }
          case 404: {
            attempts = maxRetries;
            throw new Error("The session has expired");
          }
          default: {
            throw new Error("Upload chunk failed");
          }
        }
      } catch (error) {
        attempts++;
        if (attempts < maxRetries && error.message === "Upload chunk failed") {
          console.warn("retry uploadChunk", error, attempts);
          return await uploadChunk(taskId, url, chunk, start, end, totalSize);
        } else {
          throw error;
        }
      }
    }

    //return [];
  };

  const checkRes = (res) => {
    if (res.status === 401) {
      showToast(1, "Google authentication has expired, please log in again.");
      throw new Error("Token Expired");
    }
    if (res.status !== 200) {
      showToast(
        1,
        "Connect to Google Drive folder failed.Please try again later.",
      );
      throw new Error("Failed to connect to Google Drive folder");
    }
  };

  const initDriveFolder = async () => {
    try {
      // 创建文件夹，获取文件夹id：caseid文件夹, alert文件夹，archive文件夹, alert命名规则：camera_alert_2021-09-01_12-00-00
      let rootCaseFolderId, caseFolderId, alertFolderId, archiveFolderId;
      const googleToken = await getGoogleToken();
      const searchRootFolderRes = await searchFolderProperty(
        googleToken,
        "agi7",
        "Case Uploads",
      );

      checkRes(searchRootFolderRes);
      if (searchRootFolderRes?.data?.files.length === 0) {
        const createRes = await createFolder(
          googleToken,
          "Case Uploads",
          null,
          "agi7",
          "Case Uploads",
        );
        checkRes(createRes);
        console.log("createRes:", createRes);
        rootCaseFolderId = createRes?.data?.id;
      } else {
        rootCaseFolderId = searchRootFolderRes?.data?.files[0].id;
      }

      const searchCaseFolderRes = await searchFolderProperty(
        googleToken,
        "agi7",
        `Case_${caseInfo?.displayId}`,
      );
      checkRes(searchCaseFolderRes);
      if (searchCaseFolderRes?.data?.files.length === 0) {
        const createRes = await createFolder(
          googleToken,
          `Case_${caseInfo?.displayId}`,
          rootCaseFolderId,
          "agi7",
          `Case_${caseInfo?.displayId}`,
        );
        checkRes(createRes);
        console.log("createRes:", createRes);
        caseFolderId = createRes?.data?.id;
      } else {
        caseFolderId = searchCaseFolderRes?.data?.files[0].id;
      }

      const searchAlertFolderRes = await searchFolderProperty(
        googleToken,
        "agi7",
        `${caseInfo?.displayId}_Alerts`,
      );
      checkRes(searchAlertFolderRes);
      if (searchAlertFolderRes?.data?.files.length === 0) {
        const createRes = await createFolder(
          googleToken,
          "Alerts",
          caseFolderId,
          "agi7",
          `${caseInfo?.displayId}_Alerts`,
        );
        checkRes(createRes);
        console.log("createRes:", createRes);
        alertFolderId = createRes?.data?.id;
      } else {
        alertFolderId = searchAlertFolderRes?.data?.files[0].id;
      }

      const searchArchivesFolderRes = await searchFolderProperty(
        googleToken,
        "agi7",
        `${caseInfo?.displayId}_Archives`,
      );
      checkRes(searchArchivesFolderRes);
      if (searchArchivesFolderRes?.data?.files.length === 0) {
        const createRes = await createFolder(
          googleToken,
          "Archives",
          caseFolderId,
          "agi7",
          `${caseInfo?.displayId}_Archives`,
        );
        checkRes(createRes);
        console.log("createRes:", createRes);
        archiveFolderId = createRes?.data?.id;
      } else {
        archiveFolderId = searchArchivesFolderRes?.data?.files[0].id;
      }

      setCaseFolderId(caseFolderId);
      setAlertsFolderId(alertFolderId);
      setArchivesFolderId(archiveFolderId);
    } catch (error) {
      if (error.message === "Token Expired") {
        showToast(1, "Google authentication has expired, please log in again.");
        setTasks([]);
        setUploadingTask(null);
        setDownloadingTask(null);
        setAlertSnapShotSrcMap(new Map());
        setArchiveSnapshotMap(new Map());
        Cookies.remove("googleTokenExpiresAt");
        setIsUploading(false);
        return;
      }
      showToast(1, "Init Drive Folder failed, please try again later.");
      console.error("Init Drive Folder failed", error);
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    if (isUploading) {
      setExistConfirmModalOpen(true);
      return;
    }
    handleClose();
  };

  const handleClose = () => {
    onClose?.();
  };

  const handleUpload = () => {
    setIsUploading(true);
  };

  return (
    <Modal
      className=""
      centered
      closable={false}
      title={<p className="text-xl pb-6 pt-3">Share Case to Google Drive</p>}
      open={open}
      footer={
        <div className="mt-6 mb-2">
          {globalState !== "completed" && (
            <button
              className="rounded-2xl bg-white px-6 py-4 border border-blue-500 hover:border-blue-400 text-sm  transition-colors text-blue-500 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
              onClick={handleCancel}
            >
              Cancel
            </button>
          )}

          {globalState === "uploading" && (
            <button
              disabled={true}
              className="ml-4 rounded-2xl px-6 py-4 bg-blue-500 text-sm font-medium text-white transition-colors hover:bg-blue-400 disabled:hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 disabled:opacity-50"
            >
              uploading...
            </button>
          )}
          {globalState === "completed" && (
            <button
              className="ml-4 rounded-2xl px-6 py-4 bg-blue-500 text-sm font-medium text-white transition-colors hover:bg-blue-400 disabled:hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 disabled:opacity-50"
              onClick={handleClose}
            >
              Completed
            </button>
          )}
          {globalState === "upload" && (
            <button
              disabled={!isUploading && tokenWillExpire}
              className="ml-4 rounded-2xl px-6 py-4 bg-blue-500 text-sm font-medium text-white transition-colors hover:bg-blue-400 disabled:hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 disabled:opacity-50"
              onClick={handleUpload}
            >
              Upload
            </button>
          )}
          {globalState === "retry" && (
            <button
              className="ml-4 rounded-2xl px-6 py-4 bg-blue-500 text-sm font-medium text-white transition-colors hover:bg-blue-400 disabled:hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 disabled:opacity-50"
              onClick={retryAllFail}
            >
              Retry Failed
            </button>
          )}
        </div>
      }
    >
      {tokenWillExpire ? (
        <div className="h-[140px] w-full flex flex-col items-center justify-center bg-[#F5F5F5] font-semibold py-6 gap-4 rounded-2xl">
          To share cases, please authorize Google Drive access.
          <button
            className="w-[241px] h-[44px] flex items-center justify-center gap-2 border border-[#BDBDBD] rounded-2xl text-sm font-semibold hover:text-zinc-500"
            onClick={() => loginGoogle()}
          >
            <SvgGoogle width="21" />
            Authorize Google Account
          </button>
        </div>
      ) : (
        <div className="h-[140px] w-full flex flex-col items-center justify-center bg-[#F5F5F5]  p-6 gap-4 rounded-2xl">
          <div className="flex justify-between text-[#222222] w-full">
            Current Account
            <div className="font-semibold flex gap-2">
              <SvgGoogle width="21" />
              {email}
              {!isUploading && (
                <Tooltip title="Switch Account">
                  <button
                    className="bg-[#DCE7FD] p-1 rounded-md fill-[#3E83F6] hover:bg-[#e5eaf4]"
                    onClick={loginGoogle}
                  >
                    <SvgSwicthSSO width="14" />
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
          <div className="flex justify-between text-[#222222] w-full">
            Folder Name
            <p className="font-semibold">Case Report_{caseInfo?.displayId}</p>
          </div>
          <div className="flex justify-between text-[#222222] w-full">
            Upload Path
            <p className="font-semibold">/My Drive/Case Uploads/</p>
          </div>
        </div>
      )}
      {isUploading && (
        <div>
          <div className="flex  px-3 py-2 mt-4 border-b border-zinc-300 gap-2">
            <p className="w-2/3">File Name</p>
            <p className="w-1/3">Status</p>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            {tasks.map((task) => (
              <div
                key={task.id}
                className="flex items-center  px-3 py-2 border-b border-zinc-300 gap-2"
              >
                <p className="w-2/3">{task.fileName}</p>
                <p className="w-1/3">
                  {task.status === "Pending" && (
                    <p className="font-semibold">Pending</p>
                  )}
                  {task.status === "PreLoading" && (
                    <p className="font-semibold">
                      PreLoading ({task?.progress || 0}%)
                    </p>
                  )}
                  {task.status === "Downloaded" && (
                    <p className="font-semibold">PreLoaded</p>
                  )}
                  {task.status === "Uploading" && (
                    <p className="font-semibold text-[#3E83F6]">
                      Uploading ({task?.progress || 0}%)
                    </p>
                  )}
                  {task.status === "Uploaded" && (
                    <p className="font-semibold text-[#3A855D]">Uploaded</p>
                  )}
                  {(task.status === "DownloadFailed" ||
                    task.status === "UploadFailed") && (
                    <p className="font-semibold text-[#F73D2F]">Failed</p>
                  )}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
      <Modal open={reAuthModalOpen} footer={null} centered closable={false}>
        <h1 className="text-xl font-bold">
          Google authentication has expired, please authorize again
        </h1>

        <div className="flex items-center justify-end gap-4 mt-6">
          <button
            className="ml-4 rounded-2xl px-6 py-4 bg-blue-500 text-sm font-medium text-white transition-colors hover:bg-blue-400 disabled:hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 disabled:opacity-50"
            onClick={loginGoogle}
          >
            Re-Authorize Now
          </button>
        </div>
      </Modal>
      <Modal
        open={existConfirmModalOpen}
        footer={null}
        centered
        closable={false}
      >
        <h1 className="text-xl font-bold">
          Closing will stop ongoing uploads. Are you sure you want to exit?
        </h1>

        <div className="flex items-center justify-end gap-4 mt-6">
          <button
            className="rounded-2xl bg-white px-6 py-4 border border-blue-500 hover:border-blue-400 text-sm  transition-colors text-blue-500 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            onClick={() => {
              setExistConfirmModalOpen(false);
            }}
          >
            Continue Uploading
          </button>
          <button
            className="bg-[#F73D2F] w-[174px] h-[54px] rounded-2xl flex items-center justify-center text-base font-semibold text-white transition-colors hover:bg-[#f85c50] disabled:hover:bg-[#f4aea9] disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => {
              setExistConfirmModalOpen(false);

              setTimeout(() => {
                handleClose();
              }, 200);
            }}
          >
            Stop and Exit
          </button>
        </div>
      </Modal>
    </Modal>
  );
}
